import React, { useEffect, useState } from 'react';
import { useImmer } from 'use-immer';
import { useParams } from 'react-router-dom';
import { EducatorComponent } from './educator-component';
import { EducatorState, EducatorDtoErrors } from '../../interfaces';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { CreateEducatorDto, EducatorDto } from '../../../../api-service/clients';
import { ICompanyLookup } from '../../../../components/lookups/company-lookup/interfaces';
import { ConfirmDialog } from '../../../../components/confirm-dialog';
import { ActivationKeyDialog } from '../../../../components/activation-key-dialog';
import { useMetaTitle } from '../../../../hooks';
import { usePostToTab } from '../../../search/hooks/usePostToTab';
import { useAttendanceRights } from '../../use-attendance-rights';

const client = moduleClientFactory.createAttendanceClient();

export const REFRESH_EDUCATOR_TABLE = 'REFRESH_EDUCATOR_TABLE';

export function Educator() {
  const { educatorId } = useParams();

  const [educator, setEducator] = useImmer<EducatorState>({ isDeleted: false, isDeviceEnrolled: false, hasPendingActivationKey: false });
  const [errors, setErrors] = useImmer<EducatorDtoErrors>({});
  const [assignUser, setAssignUser] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isGeneratingActivationKey, setIsGeneratingActivationKey] = useState(false);
  const [isRevokingActivationKey, setIsRevokingActivationKey] = useState(false);
  const [companyEducators, setCompanyEducators] = useState<EducatorDto[]>([]);
  const postUpdate = usePostToTab(REFRESH_EDUCATOR_TABLE);
  const { canEditEducators } = useAttendanceRights();

  useMetaTitle(educatorId ? 'Redigér underviser' : 'Opret underviser');

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [activationKey, setActivationKey] = useState<string>();
  const [isActivationKeyWarningOpen, setIsActivationKeyWarningOpen] = useState(false);
  const [isRevokeDialogOpen, setIsRevokeDialogOpen] = useState(false);

  useEffect(() => {
    async function loadData() {
      try {
        if (educatorId) {
          const educatorResult = await client.getEducatorById({ educatorId });
          if (educatorResult.educator) {
            setEducator({ ...educatorResult.educator, isDeleted: false });
            setAssignUser(!!educatorResult.educator.caseworkerId);
            await getCompanyEducators(educatorResult.educator.companyId);
          }
        }
      } catch (error) {
        console.error(error);
      }
    }
    loadData();
  }, []);

  async function handleSaveEducatorClick() {
    try {
      setIsSaving(true);
      const { companyId } = educator;
      const caseworkerId = assignUser ? educator.caseworkerId : undefined;
      if (educatorId && companyId) {
        const educatorDto: EducatorDto = { ...educator, companyId, educatorId, isDeleted: false, caseworkerId };
        await client.updateEducator({ educatorDto });
      } else if (companyId) {
        const educatorDto: CreateEducatorDto = { ...educator, companyId, isDeleted: false, caseworkerId };
        await client.createEducator({ educatorDto });
      }
      postUpdate(window.opener);
      window.close();
    } catch (error) {
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  }

  function handleDeleteClick() {
    setIsDeleteDialogOpen(true);
  }

  async function handleDelete() {
    try {
      setIsDeleting(true);
      await client.deleteEducator({ educatorId });
      setIsDeleteDialogOpen(false);
      postUpdate(window.opener);
      window.close();
    } catch (error) {
      console.error(error);
    } finally {
      setIsDeleting(false);
    }
  }
  function handleGenerateActivationKeyClick() {
    if (educator.isDeviceEnrolled || educator.hasPendingActivationKey) {
      setIsActivationKeyWarningOpen(true);
      return;
    }
    generateActivationKey();
  }

  async function generateActivationKey() {
    if (!educatorId) {
      return;
    }
    setIsActivationKeyWarningOpen(false);
    try {
      setIsGeneratingActivationKey(true);
      const result = await client.generateEducatorActivationKey({ educatorId });
      setActivationKey(result.activationKey);
      const educatorResult = await client.getEducatorById({ educatorId });
      if (educatorResult.educator) {
        setEducator({ ...educatorResult.educator, isDeleted: false });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsGeneratingActivationKey(false);
    }
  }

  async function revokeActivationKey() {
    if (!educatorId) {
      return;
    }
    setIsRevokeDialogOpen(false);
    try {
      setIsRevokingActivationKey(true);
      await client.revokeEducatorActivationKey({ educatorId });
      const educatorResult = await client.getEducatorById({ educatorId });
      if (educatorResult.educator) {
        setEducator({ ...educatorResult.educator, isDeleted: false });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsRevokingActivationKey(false);
    }
  }

  async function getCompanyEducators(companyId?: string) {
    if (!companyId) {
      setCompanyEducators([]);
      return;
    }
    const companyEducatorsResult = await client.getEducatorsByCompanyId({ companyId });
    setCompanyEducators(companyEducatorsResult.educators || []);
  }

  function handleValidate() {
    const tmpErrors = {} as EducatorDtoErrors;
    let isValid = true;
    const { caseworkerId, name, companyId } = educator;

    if (!companyId) {
      tmpErrors.companyId = 'Virksomhed skal angives';
      isValid = false;
    }
    if (assignUser && !caseworkerId) {
      tmpErrors.caseworkerId = 'Sagsbehandler skal angives';
      isValid = false;
    }
    if (!assignUser && !name) {
      tmpErrors.name = 'Navn skal angives';
      isValid = false;
    }
    if (assignUser && caseworkerId && companyEducators.some(e => e.caseworkerId === caseworkerId && e.educatorId !== educatorId)) {
      tmpErrors.caseworkerId = 'Sagsbehandler er allerede oprettet som underviser i denne virksomhed';
      isValid = false;
    }

    setErrors({ ...tmpErrors });
    return isValid;
  }

  async function handleCompanyChange(value?: ICompanyLookup) {
    setEducator(draft => {
      draft.companyId = value?.id;
    });
    await getCompanyEducators(value?.id);
  }

  function handleStringChange<K extends keyof EducatorDto>(name: K, value: string) {
    setEducator((draft: EducatorDto) => {
      (draft as any)[name] = value;
    });
  }

  function handleAssignUserChange(value: boolean) {
    setAssignUser(value);
  }

  function handleCaseworkerChange(id?: string) {
    setEducator(draft => {
      draft.caseworkerId = id;
    });
  }
  return (
    <>
      <EducatorComponent
        educator={educator}
        isEditmode={!!educatorId}
        isReadOnly={!canEditEducators}
        isSaving={isSaving}
        isDeleting={isDeleting}
        isGeneratingActivationKey={isGeneratingActivationKey}
        isRevokingActivationKey={isRevokingActivationKey}
        onSaveEducatorClick={handleSaveEducatorClick}
        onValidate={handleValidate}
        onStringChange={handleStringChange}
        assignUser={assignUser}
        onAssignUserChange={handleAssignUserChange}
        onCompanyChange={handleCompanyChange}
        errors={errors}
        onDeleteClick={handleDeleteClick}
        onGenerateActivationKeyClick={handleGenerateActivationKeyClick}
        onRevokeActivationKeyClick={() => setIsRevokeDialogOpen(true)}
        onCaseworkerChange={handleCaseworkerChange}
      />
      {isDeleteDialogOpen && (
        <ConfirmDialog
          onConfirmClick={handleDelete}
          open
          title="Vil du slette denne underviser?"
          confirmButtonText="Ja, slet"
          onCancelClick={() => setIsDeleteDialogOpen(false)}
          cancelButtonText="Afbryd"
          type="warning"
        />
      )}
      {isActivationKeyWarningOpen && (
        <ConfirmDialog
          open
          onConfirmClick={generateActivationKey}
          title="Generér ny aktiveringsnøgle?"
          confirmButtonText="Generér ny nøgle"
          onCancelClick={() => setIsActivationKeyWarningOpen(false)}
          cancelButtonText="Afbryd"
          type="warning"
          message={`'${educator.name}' har allerede en aktiveringsnøgle eller en tilknyttet enhed. En ny nøgle erstatter den nuværende og tilbagekalder enhedens adgang.`}
        />
      )}
      {isRevokeDialogOpen && (
        <ConfirmDialog
          open
          onConfirmClick={revokeActivationKey}
          title="Vil du tilbagekalde aktiveringsnøglen?"
          confirmButtonText="Ja, tilbagekald"
          onCancelClick={() => setIsRevokeDialogOpen(false)}
          cancelButtonText="Afbryd"
          type="warning"
          message={`Aktiveringsnøglen og enhedens adgang for '${educator.name}' tilbagekaldes. Allerede udstedte adgangstokens er gyldige, til de udløber.`}
        />
      )}
      {activationKey && <ActivationKeyDialog activationKey={activationKey} onConfirmClick={() => setActivationKey(undefined)} />}
    </>
  );
}
